import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './TemplatesTypes.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { motion } from "motion/react";

const agentTemplates = [
    { id: 'agent-base', title: 'Base', description: 'A solid foundation with essential features and a clean design.', img: require('../../assets/imgs/previews/agent/base.jpg') },
    { id: 'agent-luxury', title: 'Luxury', description: 'Elegant and sophisticated with high-quality visuals.', img: require('../../assets/imgs/previews/agent/luxury.jpg') },
    { id: 'agent-metropolitan', title: 'Metropolitan', description: 'Modern living with neutral tones and refined visuals.', img: require('../../assets/imgs/previews/agent/metropolitan.jpg') },
    { id: 'agent-skyline', title: 'Skyline', description: 'Condo-focused theme with high-rise and city views.', img: require('../../assets/imgs/previews/agent/skyline.jpg') },
    { id: 'agent-vibrance', title: 'Vibrance', description: 'Energetic, bold, and full of life.', img: require('../../assets/imgs/previews/agent/vibrance.jpg') },
    { id: 'agent-futuristic', title: 'Futuristic', description: 'High-tech aesthetics with vibrant neon and abstract visuals.', img: require('../../assets/imgs/previews/agent/futuristic.jpg') },
    { id: 'agent-coastal', title: 'Coastal', description: 'Relaxed and breezy with a beach-inspired palette.', img: require('../../assets/imgs/previews/agent/coastal.jpg') },
    { id: 'agent-rural', title: 'Rural', description: 'Peaceful and serene with natural landscapes.', img: require('../../assets/imgs/previews/agent/rural.jpg') },
    { id: 'agent-nature', title: 'Nature', description: 'Organic and earthy with natural textures.', img: require('../../assets/imgs/previews/agent/nature.jpg') },
    { id: 'agent-noir', title: 'Noir', description: 'Sophisticated and elegant with a dark color palette.', img: require('../../assets/imgs/previews/agent/noir.jpg') },
    { id: 'agent-cottage', title: 'Cottage', description: 'A warm and cozy design with rustic charm and inviting natural elements.', img: require('../../assets/imgs/previews/agent/cottage.jpg') },
    { id: 'agent-harbour', title: 'Harbour', description: 'A fresh coastal-inspired design with clean layouts and calming waterfront aesthetics.', img: require('../../assets/imgs/previews/agent/harbour.jpg') },
    { id: 'agent-heritage', title: 'Heritage', description: 'A timeless design featuring classic architecture and elegant traditional details.', img: require('../../assets/imgs/previews/agent/heritage.jpg') },
    { id: 'agent-estate', title: 'Estate', description: 'A refined design with spacious layouts and a polished, upscale feel.', img: require('../../assets/imgs/previews/agent/estate.jpg') },
    { id: 'agent-signature', title: 'Signature', description: 'A premium design with bold styling and a distinctive, professional look.', img: require('../../assets/imgs/previews/agent/signature.jpg') },
    { id: 'agent-summit', title: 'Summit', description: 'A sleek, contemporary design with bold visuals and a confident, elevated style.', img: require('../../assets/imgs/previews/agent/summit.jpg') },
];

const AgentTemplates = () => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTemplates = agentTemplates.filter(template =>
        template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => a.title.localeCompare(b.title));

    return (
        <>
            <Helmet>
                <title>Agent Website Templates | AptoWebsites</title>
                <meta name="description" content="Explore modern, customizable website templates for real estate agents. Mobile-optimized, SEO-friendly, and easy to use." />
                <meta name="keywords" content="agent website templates, real estate agent templates, mobile, SEO, AptoWebsites" />
                <meta property="og:title" content="Agent Website Templates | AptoWebsites" />
                <meta property="og:description" content="Explore modern, customizable website templates for real estate agents. Mobile-optimized, SEO-friendly, and easy to use." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://aptowebsites.com/agent-templates" />
                <meta property="og:image" content="https://aptowebsites.com/og-image.jpg" />
                <link rel="canonical" href="https://aptowebsites.com/agent-templates" />
            </Helmet>
            <div className="templatebackground">
                <div className="template-header-bar professional">
                    <div className="template-header-titles">
                        <h1 className="template-header-title">Agent Website Templates</h1>
                        <p className="template-header-subtitle">
                            Modern designs for personal branding, listing showcases, and high-intent lead capture.
                        </p>
                    </div>
                    <div className="template-header-controls">
                        <div className="template-search-group">
                            <div className='icon-wrapper'>
                                <FontAwesomeIcon icon={faSearch} className='search-icon' />
                            </div>

                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="filter-input template-search-input"
                            />
                        </div>
                        <div className="template-results-count" aria-live="polite">
                            {filteredTemplates.length} templates
                        </div>
                    </div>
                </div>
                <div className="template-section">
                    <div className="template-list">
                        {filteredTemplates.map(template => {
                            let mobileImgSrc;
                            try {
                                mobileImgSrc = require(`../../assets/imgs/previews/mobile/agent/${template.id.replace('agent-', '')}.jpg`);
                            } catch (e) {
                                mobileImgSrc = template.img; // fallback
                            }
                            return (
                                <motion.div
                                    key={template.id}
                                    className="template-box"
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.45, ease: "easeOut" }}
                                    whileHover="hover"
                                    animate="rest"
                                >
                                    <Link to={`/${template.id}`}>
                                        <motion.div className="template-img-wrapper overlap"
                                        >
                                            <img
                                                src={require('../../assets/imgs/mockups/laptop-mockup1.png')}
                                                alt="Laptop Mockup"
                                                className="mockup-frame"
                                            />

                                            <div className="screen-img">
                                                <img
                                                    src={template.img}
                                                    alt={template.title}
                                                    className="template-img main-img"
                                                />
                                            </div>
                                            <div className="phone-mockup-container overlap">
                                                <img
                                                    src={require('../../assets/imgs/mockups/phone-mockup-transparent.png')}
                                                    alt="Phone Mockup"
                                                    className="phone-mockup-img"
                                                />
                                                <img
                                                    src={mobileImgSrc}
                                                    alt={`${template.title} mobile`}
                                                    className="template-img phone-inside-img"
                                                />
                                            </div>
                                        </motion.div>
                                        <span className="template-card-label">{template.title}</span>
                                        <motion.div
                                            className="template-info overlay"
                                            variants={{
                                                rest: {
                                                    y: 0
                                                },
                                                hover: {
                                                    y: -4
                                                }
                                            }}

                                            transition={{ duration: 0.4, ease: "easeInOut" }}
                                        >
                                            <h3 className="template-title">{template.title}</h3>

                                            <div className="template-extra">
                                                <p>{template.description}</p>
                                            </div>
                                        </motion.div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div >
            </div >
        </>
    );
};

export default AgentTemplates;
